import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { APIURL } from '../../config';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    CircularProgress,
    Button,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import ContactsIcon from '@mui/icons-material/Contacts';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NotesIcon from '@mui/icons-material/Notes';

const HouseholdOverview = ({ household_id, customerLabel = 'Customers', onNavigateToTasks, user }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [household, setHousehold] = useState(null);
    const [members, setMembers] = useState([]);
    const [upcomingTasks, setUpcomingTasks] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            if (!household_id) return;

            setIsLoading(true);
            try {
                // Fetch household details
                const householdResponse = await axios.get(`${APIURL}/households/${household_id}`);
                setHousehold(householdResponse.data);

                // Fetch household members
                const membersResponse = await axios.get(`${APIURL}/households/members`, {
                    params: { household_id }
                });
                const householdMembers = membersResponse.data || [];
                setMembers(householdMembers);

                const customerMembers = householdMembers.filter(member => member.customer_id !== null);
                const projectsByCustomer = await Promise.all(customerMembers.map(member =>
                    axios.get(`${APIURL}/project`, { params: { cus_id: member.customer_id } })
                        .then(response => ({
                            customer_name: member.cus_name,
                            projects: response.data || []
                        }))
                        .catch(error => {
                            console.error(`Error fetching projects for customer ${member.customer_id}:`, error);
                            return { customer_name: member.cus_name, projects: [] };
                        })
                ));

                const allProjects = projectsByCustomer.flatMap(item =>
                    item.projects.map(project => ({
                        ...project,
                        customer_name: item.customer_name
                    }))
                );

                const tasksByProject = await Promise.all(allProjects.map(project =>
                    axios.get(`${APIURL}/task`, { params: { pro_id: project.pro_id } })
                        .then(response => (response.data || []).map(task => ({
                            ...task,
                            project_name: project.pro_title,
                            customer_name: project.customer_name
                        })))
                        .catch(error => {
                            console.error(`Error fetching tasks for project ${project.pro_id}:`, error);
                            return [];
                        })
                ));

                const tasks = tasksByProject.flat()
                    .filter(task => {
                        const endTime = moment(task.tas_end_time);
                        return !task.tas_end_time || !endTime.isValid() || endTime.isSameOrAfter(moment(), 'day');
                    })
                    .sort((a, b) => moment(a.tas_end_time || a.tas_start_time || a.tas_created_at).diff(moment(b.tas_end_time || b.tas_start_time || b.tas_created_at)))
                    .slice(0, 5);
                setUpcomingTasks(tasks);

                const noteResults = await Promise.all(customerMembers.map(member =>
                    axios.get(`${APIURL}/comments`, { params: { customer_id: member.customer_id, empID: user?.emp_id } })
                        .then(async response => {
                            const notes = response.data || [];
                            const detailedNotes = await Promise.all(notes.slice(0, 5).map(note =>
                                axios.get(`${APIURL}/comments/details`, { params: { comment_id: note.comment_id, empID: user?.emp_id } })
                                    .then(detailsResponse => ({
                                        ...(detailsResponse.data?.[0] || note),
                                        customer_name: member.cus_name
                                    }))
                                    .catch(() => ({
                                        ...note,
                                        customer_name: member.cus_name
                                    }))
                            ));
                            return detailedNotes;
                        })
                        .catch(error => {
                            console.error(`Error fetching notes for customer ${member.customer_id}:`, error);
                            return [];
                        })
                ));

                const activities = noteResults.flat()
                    .filter(note => note.created_at || note.updated_at)
                    .sort((a, b) => moment(b.updated_at || b.created_at).diff(moment(a.updated_at || a.created_at)))
                    .slice(0, 8);
                setRecentActivity(activities);
            } catch (error) {
                console.error('Error fetching household data:', error);
                setMembers([]);
                setUpcomingTasks([]);
                setRecentActivity([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [household_id]);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!household) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">No household data available</Typography>
            </Box>
        );
    }

    const customerCount = members.filter(m => m.customer_id).length;
    const contactCount = members.filter(m => m.contact_id).length;
    const totalMembers = customerCount + contactCount;
    const customerMembers = members.filter(member => member.customer_id !== null);
    const contactMembers = members.filter(member => member.contact_id !== null);
    const middleTableColumnWidths = {
        role: '31%',
        name: '31%',
        contact: '38%'
    };
    const palette = {
        accent: '#3FA7D6',
        accentDark: '#1565c0',
        accentSoft: '#EBF8FF',
        border: '#e0ecfa',
        borderStrong: '#c5d8f0',
        headerBg: '#F9F9F9',
        pageBg: '#ffffff',
        text: '#222',
        muted: '#666'
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const getAddressLines = () => {
        const street = [household.loc_addr_line1, household.loc_addr_line2].filter(Boolean).join(', ');
        const cityStateZip = [
            household.loc_city,
            [household.loc_state, household.loc_zip].filter(Boolean).join(' ')
        ].filter(Boolean).join(', ');

        return [street, cityStateZip].filter(Boolean);
    };

    const copyAddress = () => {
        const address = getAddressLines().join('\n');
        if (address && navigator.clipboard) {
            navigator.clipboard.writeText(address);
        }
    };

    const getMemberName = (member) => {
        if (member.member_type === 'customer') {
            return member.cus_name || `Unnamed ${customerLabel.replace(/s$/, '')}`;
        }

        return `${member.con_first_name || ''} ${member.con_last_name || ''}`.trim() || 'Unnamed Contact';
    };

    const getMemberRole = (member, index) => {
        return member.household_role || member.member_role || member.relationship || (index === 0 ? 'Primary Contact' : 'Alternate Contact');
    };

    const getMemberEmail = (member) => {
        return member.member_type === 'customer' ? member.cus_email : member.con_email;
    };

    const getMemberPhone = (member) => {
        return member.member_type === 'customer' ? member.cus_phone : member.con_phone;
    };

    const formatContact = (member) => {
        return [getMemberPhone(member), getMemberEmail(member)].filter(Boolean).join(' / ') || '-';
    };

    const getActivityName = (activity) => {
        return `${activity.updated_emp_first_name || activity.emp_first_name || 'Someone'} ${activity.updated_emp_last_name || activity.emp_last_name || ''}`.trim();
    };

    const getTaskDate = (task) => {
        const date = task.tas_end_time || task.tas_start_time || task.tas_created_at;
        return moment(date).isValid() ? moment(date).format('MMM DD, YYYY') : 'No due date';
    };

    const SectionCard = ({ title, children, sx }) => (
        <Card
            elevation={0}
            sx={{
                border: `1px solid ${palette.border}`,
                borderRadius: '6px',
                backgroundColor: '#fff',
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                ...sx
            }}
        >
            <Box sx={{ px: 1.5, py: 1, backgroundColor: palette.headerBg, borderBottom: `1px solid ${palette.border}` }}>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: palette.text, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    {title}
                </Typography>
            </Box>
            <Box sx={{ px: 1.5, pb: 1.5, flex: 1, minHeight: 0, overflow: 'auto' }}>
                {children}
            </Box>
        </Card>
    );

    return (
        <Box sx={{ p: 2, backgroundColor: palette.pageBg }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '250px minmax(360px, 1fr) 310px' },
                    gap: 2,
                    alignItems: 'stretch'
                }}
            >
                <Box>
                    <Card elevation={0} sx={{ borderRadius: '6px', border: `1px solid ${palette.border}`, mb: 1.5 }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 3 }}>
                                <HomeIcon sx={{ color: palette.accent, fontSize: 30 }} />
                                <Box>
                                    <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.15 }}>
                                        {household.household_name}
                                    </Typography>
                                    <Chip
                                        label={household.is_actively_serviced ? 'Active' : 'Inactive'}
                                        size="small"
                                        sx={{
                                            mt: 0.5,
                                            height: 20,
                                            fontSize: 11,
                                            color: '#fff',
                                            backgroundColor: household.is_actively_serviced ? '#39a852' : '#8b928b'
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Divider sx={{ mb: 1.5 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1 }}>
                                Household Details
                            </Typography>

                            {household.household_description && (
                                <Box sx={{ mb: 1.25 }}>
                                    <Typography sx={{ fontSize: 12, color: palette.muted, fontWeight: 700 }}>Description</Typography>
                                    <Typography sx={{ fontSize: 13, lineHeight: 1.25 }}>{household.household_description}</Typography>
                                </Box>
                            )}

                            {getAddressLines().length > 0 && (
                                <Box sx={{ display: 'flex', gap: 1, mb: 1.25, alignItems: 'flex-start' }}>
                                    <LocationOnIcon sx={{ color: palette.accent, fontSize: 18, mt: 0.25 }} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 12, color: palette.muted, fontWeight: 700 }}>Location</Typography>
                                        {getAddressLines().map(line => (
                                            <Typography key={line} sx={{ fontSize: 13, lineHeight: 1.25 }}>{line}</Typography>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {getAddressLines().length > 0 && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ContentCopyIcon />}
                                    onClick={copyAddress}
                                    sx={{ mb: 1.5, fontSize: 11, py: 0.25, borderColor: palette.borderStrong, color: palette.accentDark, fontWeight: 600 }}
                                >
                                    Copy Address
                                </Button>
                            )}

                            <Box sx={{ display: 'grid', gap: 1 }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                    <CalendarTodayIcon sx={{ color: palette.accent, fontSize: 17 }} />
                                    <Box>
                                        <Typography sx={{ fontSize: 12, color: palette.muted, fontWeight: 700 }}>Date Added</Typography>
                                        <Typography sx={{ fontSize: 13 }}>{formatDate(household.date_added)}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                    <CalendarTodayIcon sx={{ color: palette.accent, fontSize: 17 }} />
                                    <Box>
                                        <Typography sx={{ fontSize: 12, color: palette.muted, fontWeight: 700 }}>Last Visited</Typography>
                                        <Typography sx={{ fontSize: 13 }}>{formatDate(household.date_last_visited)}</Typography>
                                    </Box>
                                </Box>
                                {household.added_by_first_name && (
                                    <Box>
                                        <Typography sx={{ fontSize: 12, color: palette.muted, fontWeight: 700 }}>Added By</Typography>
                                        <Typography sx={{ fontSize: 13 }}>{household.added_by_first_name} {household.added_by_last_name}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
                        {[
                            { icon: <PeopleIcon />, value: totalMembers, label: 'Members', color: palette.accent },
                            { icon: <PersonIcon />, value: customerCount, label: customerLabel.replace(/s$/, ''), color: '#3e9b41' },
                            { icon: <ContactsIcon />, value: contactCount, label: 'Contacts', color: '#c98624' }
                        ].map(stat => (
                            <Card key={stat.label} elevation={0} sx={{ border: `1px solid ${palette.border}`, borderRadius: '5px', backgroundColor: '#fff' }}>
                                <CardContent sx={{ textAlign: 'center', p: 1, '&:last-child': { pb: 1 } }}>
                                    <Box sx={{ color: stat.color, display: 'flex', justifyContent: 'center', '& svg': { fontSize: 24 } }}>
                                        {stat.icon}
                                    </Box>
                                    <Typography sx={{ fontSize: 22, fontWeight: 700, color: stat.color, lineHeight: 1 }}>{stat.value}</Typography>
                                    <Typography sx={{ fontSize: 11, color: palette.muted, lineHeight: 1.15 }}>{stat.label}</Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ display: 'grid', gap: 2, gridTemplateRows: { xs: 'auto', lg: 'repeat(2, minmax(0, 1fr))' }, minHeight: 0 }}>
                    <SectionCard title="Key Household Members">
                        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${palette.border}` }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: palette.accentSoft }}>
                                        <TableCell sx={{ color: palette.text, fontWeight: 700, fontSize: 14, width: middleTableColumnWidths.role, borderBottom: `1px solid ${palette.border}` }}>Role</TableCell>
                                        <TableCell sx={{ color: palette.text, fontWeight: 700, fontSize: 14, width: middleTableColumnWidths.name, borderBottom: `1px solid ${palette.border}` }}>Name</TableCell>
                                        <TableCell sx={{ color: palette.text, fontWeight: 700, fontSize: 14, width: middleTableColumnWidths.contact, borderBottom: `1px solid ${palette.border}` }}>Contact Info</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(customerMembers.length > 0 ? customerMembers : members).slice(0, 5).map((member, index) => (
                                        <TableRow key={member.household_member_id || index}>
                                            <TableCell sx={{ fontSize: 14, width: middleTableColumnWidths.role }}>{getMemberRole(member, index)}</TableCell>
                                            <TableCell sx={{ fontSize: 14, width: middleTableColumnWidths.name }}>{getMemberName(member)}</TableCell>
                                            <TableCell sx={{ fontSize: 14, width: middleTableColumnWidths.contact }}>{formatContact(member)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {members.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} sx={{ fontSize: 14, color: palette.muted }}>No household members found.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </SectionCard>

                    <SectionCard title="Key Household Contacts">
                        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${palette.border}` }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: palette.accentSoft }}>
                                        <TableCell sx={{ color: palette.text, fontWeight: 700, fontSize: 14, width: middleTableColumnWidths.role, borderBottom: `1px solid ${palette.border}` }}>Contact Role</TableCell>
                                        <TableCell sx={{ color: palette.text, fontWeight: 700, fontSize: 14, width: middleTableColumnWidths.name, borderBottom: `1px solid ${palette.border}` }}>Contact Name</TableCell>
                                        <TableCell sx={{ color: palette.text, fontWeight: 700, fontSize: 14, width: middleTableColumnWidths.contact, borderBottom: `1px solid ${palette.border}` }}>Contact Info</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {contactMembers.slice(0, 5).map((member, index) => (
                                        <TableRow key={member.household_member_id || index}>
                                            <TableCell sx={{ fontSize: 14, width: middleTableColumnWidths.role }}>{getMemberRole(member, index)}</TableCell>
                                            <TableCell sx={{ fontSize: 14, width: middleTableColumnWidths.name }}>{getMemberName(member)}</TableCell>
                                            <TableCell sx={{ fontSize: 14, width: middleTableColumnWidths.contact }}>{formatContact(member)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {contactMembers.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} sx={{ fontSize: 14, color: palette.muted }}>No household contacts found.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </SectionCard>
                </Box>

                <Box sx={{ display: 'grid', gap: 2, gridTemplateRows: { xs: 'auto', lg: 'repeat(2, minmax(0, 1fr))' }, minHeight: 0 }}>
                    <SectionCard title="Upcoming Tasks">
                        {upcomingTasks.length === 0 ? (
                            <Typography sx={{ fontSize: 12, color: palette.muted, px: 0.5, py: 1 }}>No upcoming tasks found.</Typography>
                        ) : (
                            <List disablePadding sx={{ display: 'grid', gap: 0.75 }}>
                                {upcomingTasks.map(task => (
                                    <ListItem
                                        key={task.tas_id}
                                        disablePadding
                                        onClick={onNavigateToTasks}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                onNavigateToTasks?.();
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        secondaryAction={
                                            <IconButton edge="end" size="small" aria-label="View household tasks" tabIndex={-1}>
                                                <ChevronRightIcon />
                                            </IconButton>
                                        }
                                        sx={{
                                            backgroundColor: palette.accentSoft,
                                            border: `1px solid ${palette.border}`,
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                            px: 1,
                                            py: 0.75,
                                            '&:hover': { backgroundColor: palette.border },
                                            '&:focus-visible': { outline: `2px solid ${palette.accent}`, outlineOffset: 2 }
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 30 }}>
                                            <AssignmentIcon sx={{ color: '#a92432', fontSize: 20 }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Typography sx={{ fontSize: 12, fontWeight: 700 }}>{task.tas_title || 'Untitled Task'}</Typography>}
                                            secondary={<Typography sx={{ fontSize: 11, color: '#a92432' }}>Due {getTaskDate(task)}</Typography>}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </SectionCard>

                    <SectionCard title="Recent Activity">
                        {recentActivity.length === 0 ? (
                            <Typography sx={{ fontSize: 12, color: palette.muted, px: 0.5, py: 1 }}>No recent activity found.</Typography>
                        ) : (
                            <List disablePadding sx={{ maxHeight: 270, overflowY: 'auto' }}>
                                {recentActivity.map((activity, index) => {
                                    const activityTime = activity.updated_at || activity.created_at;
                                    return (
                                        <ListItem key={activity.comment_id || activity.id || index} disablePadding sx={{ py: 0.6, alignItems: 'flex-start' }}>
                                            <ListItemIcon sx={{ minWidth: 32, pt: 0.25 }}>
                                                <Box sx={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: palette.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <NotesIcon sx={{ fontSize: 15, color: palette.accent }} />
                                                </Box>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography sx={{ fontSize: 12, lineHeight: 1.2 }}>
                                                        <strong>{getActivityName(activity)}</strong> {activity.updated_at ? 'updated' : 'added'} a household note
                                                    </Typography>
                                                }
                                                secondary={<Typography sx={{ fontSize: 11, color: palette.muted }}>{moment(activityTime).fromNow()}</Typography>}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        )}
                    </SectionCard>
                </Box>
            </Box>
        </Box>
    );
};

export default HouseholdOverview;
